import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useAppStore } from '../stores/UnifiedStoreProvider';
import './AppInitializer.css';

interface AppInitializerProps {
  children: React.ReactNode;
}

/**
 * UNIFIED APP INITIALIZER
 * Handles initialization of the unified AppStore
 * - Token verification first
 * - Fresh data loading from workspace endpoint
 * - No persistence, everything loaded from API
 * - Wrapped in observer() to react to auth state changes
 */
const UnifiedAppInitializer: React.FC<AppInitializerProps> = observer(({ children }) => {
  console.log('🎬 [UnifiedAppInitializer] RENDER START');
  
  // ALL hooks must be called at the top level, in the same order every render
  const appStore = useAppStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useStytchMemberSession(); // CHECK STYTCH SESSION FIRST
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initStartTime] = useState(Date.now());
  const hasInitialized = useRef(false);
  const hasNavigated = useRef(false); // Track if we've already navigated
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // On public routes, appStore might be null - just render children immediately
  if (!appStore) {
    console.log('🎬 [UnifiedAppInitializer] No store on public route, rendering children');
    return <>{children}</>;
  }
  
  console.log('🎬 [UnifiedAppInitializer] State:', { 
    isInitialized, 
    error, 
    hasInitRef: hasInitialized.current,
    hasStytchSession: !!session 
  });

  // Timeout effect - if not initialized after 5 seconds, show error
  useEffect(() => {
    if (isInitialized || error) {
      // Already initialized or errored, clear any timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Set timeout for 30 seconds (increased for debugging slow backend)
    timeoutRef.current = setTimeout(() => {
      const elapsed = Date.now() - initStartTime;
      console.error(`❌ [UnifiedAppInitializer] Initialization timeout after ${elapsed}ms`);
      console.error('   Session:', !!session);
      console.error('   Auth:', appStore.auth?.isAuthenticated);
      console.error('   Store initialized:', appStore.isInitialized);
      
      setError('App failed to initialize. Please try again.');
    }, 30000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isInitialized, error, session, appStore, initStartTime]);

  // Sync local state with appStore state (read once per effect run, don't watch observables)
  useEffect(() => {
    const storeInitialized = appStore.isInitialized;
    if (storeInitialized && !isInitialized) {
      console.log('[UnifiedAppInitializer] AppStore is initialized, updating local state');
      setIsInitialized(true);
    }
  }, [appStore, isInitialized]);

  // Track auth state changes with a simple reactive value
  const isAuthSynced = appStore.auth?.isAuthenticated || false;

  useEffect(() => {
    // Read auth state once at start of effect, don't create dependencies on observables
    const auth = appStore.auth;
    const authState = {
      isAuthenticated: auth?.isAuthenticated,
      isLoading: auth?.isLoading,
      hasUser: !!auth?.user
    };
    
    console.log('🎬 [UnifiedAppInitializer] useEffect FIRED, auth state:', {
      ...authState,
      pathname: location.pathname,
      isInitialized,
      isAuthSynced
    });
    
    // If already initialized, don't run logic again
    if (isInitialized) {
      console.log('[UnifiedAppInitializer] Already initialized, skipping');
      return;
    }
    
    // Check if we're on a public route that doesn't require auth
    const publicRoutes = ['/login', '/signup', '/auth'];
    const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));
    
    // If on a public route, just render the page immediately (no init needed)
    if (isPublicRoute) {
      console.log('[UnifiedAppInitializer] On public route, no initialization needed');
      setIsInitialized(true);
      return;
    }
    
    // CRITICAL: Check Stytch session FIRST before allowing access to protected routes
    if (!session && !hasNavigated.current) {
      console.log('🚫 [UnifiedAppInitializer] No Stytch session on protected route, redirecting to /login');
      hasNavigated.current = true;
      navigate('/login', { replace: true });
      setIsInitialized(true);
      return;
    }
    
    // Wait for StytchSessionSync to populate auth state from Stytch session
    // But don't wait forever - the timeout will catch this
    if (session && !authState.isAuthenticated && !authState.isLoading) {
      console.log('⏳ [UnifiedAppInitializer] Stytch session exists but auth not synced yet, waiting...');
      
      // If we've been waiting too long, something is wrong
      const elapsed = Date.now() - initStartTime;
      if (elapsed > 4000) {
        console.error('❌ [UnifiedAppInitializer] Auth sync taking too long, likely failed');
        setError('Failed to sync authentication. Your session may be invalid.');
        return;
      }
      
      return;
    }
    
    // If still loading auth, wait
    if (authState.isLoading) {
      console.log('⏳ [UnifiedAppInitializer] Auth still loading, waiting...');
      return;
    }
    
    // If auth sync failed (no auth state despite Stytch session), redirect to login
    // This catches the case where Stytch session exists but backend rejects it
    if (session && !authState.isAuthenticated && !authState.isLoading && !hasNavigated.current) {
      const elapsed = Date.now() - initStartTime;
      // Only redirect after giving it a chance to sync (2+ seconds)
      if (elapsed > 2000) {
        console.error('🚫 [UnifiedAppInitializer] Auth sync failed after 2s, redirecting to /login');
        hasNavigated.current = true;
        navigate('/login', { replace: true });
        setIsInitialized(true);
        return;
      }
    }
    
    // Only initialize once - check local ref only (not appStore.isInitializing to avoid race condition)
    if (hasInitialized.current || appStore.isInitialized) {
      console.log('[UnifiedAppInitializer] Skipping init:', {
        hasInitialized: hasInitialized.current,
        storeInitialized: appStore.isInitialized
      });
      setIsInitialized(true); // Make sure we're marked as initialized
      return;
    }

    const initializeApp = async () => {
      try {
        // Skip if already initialized (e.g., from login flow)
        if (appStore.isInitialized) {
          console.log('[UnifiedAppInitializer] Already initialized, skipping');
          setIsInitialized(true);
          return;
        }
        
        console.log('[UnifiedAppInitializer] Starting initialization...');
        hasInitialized.current = true; // Mark as started immediately
        
        // Get org slug from URL (simple extraction without store access)
        const pathParts = window.location.pathname.split('/');
        let orgSlug: string | undefined = undefined;
        if (pathParts.length > 1 && pathParts[1] && !['login', 'signup', 'auth', 'public'].includes(pathParts[1])) {
          orgSlug = decodeURIComponent(pathParts[1]);
        }
        
        // Use the unified AppStore initialization
        await appStore.initializeApp(orgSlug);
        
        console.log('[UnifiedAppInitializer] Initialization complete, setting local state');
        setIsInitialized(true);
        
      } catch (error) {
        console.error('[UnifiedAppInitializer] Initialization error:', error);
        console.error('🚫 [UnifiedAppInitializer] /workspace endpoint failed - redirecting to /login');
        
        // If workspace endpoint fails, user is not properly set up
        // Redirect to login instead of showing error screen
        if (!hasNavigated.current) {
          hasNavigated.current = true;
          navigate('/login', { replace: true });
          setIsInitialized(true);
        }
      }
    };

    initializeApp();
    
    return () => {
      // Cleanup
      const auth = appStore.auth;
      if (auth && auth.isReallyAuthenticated) {
        auth.stopTokenRefreshTimer();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, appStore, isInitialized, isAuthSynced]); // Depend on isAuthSynced to re-run when auth changes

  // SIMPLIFIED: No "initializing" or error screens
  // Just render children immediately - auth guards handle login redirects
  console.log('✅ [UnifiedAppInitializer] RENDERING CHILDREN (auth guards will handle redirects)');
  return <>{children}</>;
});

export default UnifiedAppInitializer;
